import { Satellite, Layers, Map as MapIcon } from 'lucide-react';

/**
 * MapLayerControl Component
 *
 * Provides a toggle control for switching between different map visualization layers:
 * - Satellite: Esri World Imagery satellite view
 * - Hybrid: Satellite with street labels overlay
 * - Streets: OpenStreetMap standard street view
 *
 * @param {Object} props - Component props
 * @param {string} props.activeLayer - Currently active layer ('satellite' | 'hybrid' | 'streets')
 * @param {function} props.onLayerChange - Callback when layer is changed (layer) => void
 * @param {Object} props.mapInstance - Leaflet map instance from react-leaflet
 * @returns {JSX.Element} Layer control component
 */
export default function MapLayerControl({ activeLayer, onLayerChange, mapInstance }) {
  const layers = [
    {
      id: 'satellite',
      label: 'Satélite',
      icon: Satellite,
      description: 'Vista satélite'
    },
    {
      id: 'hybrid',
      label: 'Híbrido',
      icon: Layers,
      description: 'Satélite com ruas'
    },
    {
      id: 'streets',
      label: 'Ruas',
      icon: MapIcon,
      description: 'Mapa de ruas'
    }
  ];

  const handleLayerClick = (layerId) => {
    if (onLayerChange && layerId !== activeLayer) {
      onLayerChange(layerId);
    }
  };

  return (
    <div
      className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg p-2 flex gap-2"
      role="group"
      aria-label="Controle de camadas do mapa"
    >
      {layers.map((layer) => {
        const Icon = layer.icon;
        const isActive = activeLayer === layer.id;

        return (
          <button
            key={layer.id}
            onClick={() => handleLayerClick(layer.id)}
            className={`
              relative p-2 rounded-md transition-all duration-200
              ${isActive
                ? 'bg-blue-500 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            `}
            title={layer.description}
            aria-label={layer.label}
            aria-pressed={isActive}
            type="button"
          >
            <Icon className="w-5 h-5" />
            {isActive && (
              <span className="sr-only">{layer.label} ativo</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
